// packages/core/src/storage/StorageAdapter.ts
// ─────────────────────────────────────────────────────────────────────────
// Platform-agnostic key/value storage contract.
// Web: implemented with Dexie + IndexedDB
// Mobile: implemented with expo-sqlite or react-native-mmkv
// Tests: implemented with in-memory Map
// ─────────────────────────────────────────────────────────────────────────

export interface StorageAdapter {
	/** Retrieve a value by key. Returns null if not found. */
	get<T = unknown>(table: string, key: string): Promise<T | null>;

	/** Store a value. Overwrites existing. */
	set<T = unknown>(table: string, key: string, value: T): Promise<void>;

	/** Remove a value. No-op if missing. */
	delete(table: string, key: string): Promise<void>;

	/** List all keys in a table, optionally with a prefix filter. */
	listKeys(table: string, prefix?: string): Promise<string[]>;

	/** List all values in a table, optionally with a prefix filter. */
	listValues<T = unknown>(table: string, prefix?: string): Promise<T[]>;

	/** Count rows in a table. */
	count(table: string): Promise<number>;

	/** Wipe all data in a table. */
	clear(table: string): Promise<void>;

	/** Run a function with multiple operations as an atomic transaction. */
	transaction<T>(tables: string[], fn: () => Promise<T>): Promise<T>;
}

/**
 * Logical table names used across Nisk. Implementations should set up
 * these tables at initialization. Adding a new table means adding it here
 * AND in each adapter's schema setup.
 */
export const STORAGE_TABLES = {
	// Content cache
	LESSONS: 'lessons',
	MODULES: 'modules',

	// User state
	PROGRESS: 'progress',
	SRS_RECORDS: 'srsRecords',
	SETTINGS: 'settings',

	// Sync infrastructure
	SYNC_QUEUE: 'syncQueue',
	OPERATIONS: 'operations',

	// Economy (client-side cache; server is source of truth)
	ECONOMY: 'economy',

	// Downloads
	DOWNLOADS: 'downloads',
	DOWNLOAD_META: 'downloadMeta',
} as const;

